import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Header from '../Components/Header';
import PackageCard from '../Components/Cards/PackagesCard';
import { fetchTourPackagesByType, fetchImagesFromS3 } from '../redux/slices/packageSlice';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ReviewComponent from '../Components/Reviews';
import LottiePlayer from '../Components/LottiePlayer';

const Carousel = ({ images }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);

    useEffect(() => {
        const interval = setInterval(() => {
            handleNext();
        }, 5000);
        return () => clearInterval(interval);
    }, [images.length]);

    const handlePrev = () => {
        if (!isTransitioning) {
            setIsTransitioning(true);
            setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
            setTimeout(() => setIsTransitioning(false), 500);
        }
    };

    const handleNext = () => {
        if (!isTransitioning) {
            setIsTransitioning(true);
            setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
            setTimeout(() => setIsTransitioning(false), 500);
        }
    };

    const goToSlide = (index) => {
        if (!isTransitioning) {
            setIsTransitioning(true);
            setCurrentIndex(index);
            setTimeout(() => setIsTransitioning(false), 500);
        }
    };

    return (
        <div className="relative w-full h-[60vh] sm:h-[60vh] md:h-[100vh] max-h-[600px] overflow-hidden">
            <div
                className="flex h-full w-full transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
                {images.map((image, index) => (
                    <div key={index} className="min-w-full h-full flex-shrink-0">
                        <img
                            src={image}
                            alt={`Slide ${index + 1}`}
                            loading="lazy"
                            className="w-full h-full object-cover rounded-b-xl"
                        />
                    </div>
                ))}
            </div>
            <div className="absolute inset-0 bg-white opacity-10"></div>

            {/* Navigation Arrows */}
            <button
                onClick={handlePrev}
                className="absolute left-4 sm:left-10 md:left-20 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 sm:p-3 border rounded-full hover:bg-blue-700/70 z-30"
            >
                <ChevronLeft size={24} />
            </button>
            <button
                onClick={handleNext}
                className="absolute right-4 sm:right-10 md:right-20 top-1/2 transform -translate-y-1/2 bg-black/50 text-white p-2 sm:p-3 border rounded-full hover:bg-blue-700/70 z-30"
            >
                <ChevronRight size={24} />
            </button>

            {/* Dots */}
            <div className="absolute bottom-3 sm:bottom-5 left-0 right-0 flex justify-center space-x-2 z-30">
                {images.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${index === currentIndex ? 'bg-yellow-400' : 'bg-white/50'
                            } hover:bg-yellow-400/80`}
                    />
                ))}
            </div>
        </div>
    );
};

const ViewMorePage = () => {
    const { tourType } = useParams();
    const dispatch = useDispatch();

    const {
        domesticPackages,
        internationalPackages,
        shorttripPackages,
        customisedPackages,
        activitiesPackages,
        loading,
        error,
        s3Images,
        s3ImagesLoading,
        s3ImagesError,
    } = useSelector((state) => state.package);

    // Select packages based on tourType
    const displayPackages = (() => {
        switch (tourType) {
            case 'Domestic':
                return domesticPackages;
            case 'International':
                return internationalPackages;
            case 'Short':
                return shorttripPackages;
            case 'Customised':
                return customisedPackages;
            case 'Activities':
                return activitiesPackages;
            default:
                return [];
        }
    })();

    // Fallback images
    const fallbackImages = [
        'https://dbwr0bte83ose.cloudfront.net/tour-images/international/ff76bef7-7635-4154-b99e-569c26d8d4f6-extractimages_5132025_22503.jpeg',
    ];

    // Use S3 images if available, otherwise fallback
    const carouselImages = s3Images?.length > 0 ? s3Images : fallbackImages;

    useEffect(() => {
        if (tourType) {
            dispatch(fetchTourPackagesByType(tourType));
            dispatch(fetchImagesFromS3(tourType));
        }
    }, [dispatch, tourType]);

    return (
        // <div className="min-h-screen">
        <div className="w-full px-4 sm:px-6 lg:px-16 xl:px-20  max-w-7xl  mx-auto ">
            {/* Hero Section */}
            <div className="relative">
                <div className="absolute top-0 left-0 right-0 z-40">
                    <Header page="viewmore" />
                </div>

                {s3ImagesLoading ? (
                    <div className="h-[100vh] w-full flex items-center  justify-center bg-custom-blue">
                        <div className="flex items-center justify-center min-h-screen">
                            <LottiePlayer
                                src="https://lottie.host/08969d3a-af8c-41f7-8b3e-86c73abeacc9/TnjpVnGl5h.lottie"
                                width={300}
                                height={300}
                            />
                        </div>
                    </div>
                ) : s3ImagesError ? (
                    <div className="h-[100vh] w-full flex items-center justify-center bg-custom-blue">
                        <p className="text-xl text-red-500">Error loading images: {s3ImagesError}</p>
                    </div>
                ) : (
                    <Carousel images={carouselImages} />
                )}

                <div className="absolute bottom-20 left-0 right-0 flex flex-col items-center justify-center text-custom-white z-30">
                    <h1 className="text-5xl font-bold text-center">
                        <span className="text-custom-yellow">{tourType?.toUpperCase()}</span> Tour Packages!
                    </h1>
                    <hr className="mt-6 border-custom-white w-3/4 sm:w-1/2" />
                    <a href="tel:+916360204100">
                        <button className="mt-6 bg-custom-yellow text-custom-blue px-6 py-3 rounded-full hover:bg-custom-yellow/80">
                            Call Us To Know More
                        </button>
                    </a>

                </div>
            </div>

            {/* Packages Section */}
            <div className="flex flex-wrap justify-center py-10 bg-white">
                {loading ? (
                    <p className="text-xl text-gray-600">Loading packages...</p>
                ) : error ? (
                    <p className="text-xl text-red-500">Error: {error}</p>
                ) : displayPackages.length > 0 ? (
                    displayPackages.map((pkg) => (
                        <PackageCard key={pkg._id || pkg.id} pkg={pkg} />
                    ))
                ) : (
                    <p className="text-xl text-gray-600">No packages found.</p>
                )}
            </div>

            <ReviewComponent tourType={tourType} />
        </div>
    );
};

export default ViewMorePage;